import { assign, createMachine } from "xstate";
import { fetchClientData, IClient } from "@nju/data-access-sales-client";
import { fetchActionToken } from "@nju/data-access-sales-user";

type IEvent =
  | { type: "LOADED"; clientData: IClient }
  | { type: "HIDE" }
  | { type: "SHOW" }
  | { type: "ERROR" }
  | { type: "CHECK_CLIENT" }
  | { type: "CHECK_CLIENT_SUCCESS" }
  | { type: "REFRESH" };

interface IContext {
  userId: string;
  clientData: IClient;
}

export const orderMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "hidden",
    states: {
      hidden: {
        id: "hidden",
        on: {
          SHOW: {
            target: "visible",
          },
        },
      },
      visible: {
        initial: "loading",
        states: {
          loading: {
            always: [{ target: "ready", cond: "hasClientData" }],
            invoke: {
              src: "fetchUser",
            },
            on: {
              LOADED: {
                target: "ready",
                actions: ["setUserData"],
              },
              ERROR: {
                target: "error",
              },
              HIDE: {
                target: "#hidden",
              },
            },
          },
          error: {
            on: {
              HIDE: {
                target: "#hidden",
              },
              REFRESH: {
                target: "loading",
              },
            },
          },
          ready: {
            on: {
              HIDE: {
                target: "#hidden",
              },
              CHECK_CLIENT: {
                target: "checkClient",
              },
            },
          },
          checkClient: {
            invoke: {
              src: "checkClient",
            },
            on: {
              CHECK_CLIENT_SUCCESS: {
                actions: ["onClientCheckRedirect"],
              },
              ERROR: {
                target: "error",
              },
            },
          },
        },
      },
    },
  },
  {
    actions: {
      setUserData: assign((context, event: IEvent) => {
        if (event.type === "LOADED") {
          return {
            clientData: event.clientData,
          };
        }
        return context;
      }),
    },
    guards: {
      hasClientData: (context) => {
        return !!context.clientData;
      },
    },
    services: {
      fetchUser: ({ userId }) => {
        return async (callback) => {
          const result = await fetchClientData(userId);

          if (result.isError) {
            return callback({ type: "ERROR" });
          }

          callback({ type: "LOADED", clientData: result.value });
        };
      },
      checkClient: ({ clientData }) => {
        return async (callback) => {
          const checkClientInKeycloak = await fetchActionToken({
            email: clientData.email,
          });

          if (checkClientInKeycloak.isError) {
            return callback({ type: "ERROR" });
          }

          callback({ type: "CHECK_CLIENT_SUCCESS" });
        };
      },
    },
  },
);
